import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { crmAPI, analyticsAPI, targetAPI } from '../../lib/api';
import { useCurrency } from '../../lib/CurrencyContext';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Search, Filter, GripVertical, DollarSign, User, Mail, Phone, Calendar, MessageSquare, Activity, Target, TrendingUp, AlertTriangle, CheckCircle, Plus, Send, Clock, Building2, Users, Tag, Briefcase, FileText, Check, X, Edit2, Maximize2, Minimize2, Download, Settings2, ArrowUpDown, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { PageFilters, YearFilter, QuarterFilter, SalesRepFilter, AccountFilter, StageFilter, ProductDirectorFilter, SolutionCategoryFilter } from '../layout/PageFilters';
import { AdvancedFilterBuilder, FilterChipBar } from '../layout/AdvancedFilterBuilder';
import { CompactBusinessRules } from '../layout/CompactBusinessRules';
import { DataFreshnessBar } from '../layout/DataFreshnessBar';
import { RoleScopeControl } from '../layout/RoleScopeControl';
import { OpportunityDetailSheet } from './OpportunityDetailSheet';

const STAGES = ['qualified', 'proposal', 'negotiation', 'review_negotiation', 'closed_won', 'closed_lost'];

const stageColors = {
  qualified: 'bg-blue-100 text-blue-700 border-blue-200',
  proposal: 'bg-purple-100 text-purple-700 border-purple-200',
  negotiation: 'bg-amber-100 text-amber-700 border-amber-200',
  closed_won: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed_lost: 'bg-red-100 text-red-700 border-red-200',
  // Custom stages from Odoo
  'review_negotiation': 'bg-amber-100 text-amber-700 border-amber-200',
  'enquiry': 'bg-gray-100 text-gray-700 border-gray-200',
  'won': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'lost': 'bg-red-100 text-red-700 border-red-200',
  'hold': 'bg-gray-100 text-gray-600 border-gray-200',
};

const riskColors = {
  low: 'text-emerald-600 bg-emerald-50',
  medium: 'text-amber-600 bg-amber-50',
  high: 'text-red-600 bg-red-50',
};

const formatStage = (stage) => {
  if (!stage) return 'Unknown';
  // Handle special cases
  const stageMap = {
    'review_negotiation': 'Review & Negotiation',
    'closed_won': 'Closed Won',
    'closed_lost': 'Closed Lost',
  };
  if (stageMap[stage.toLowerCase()]) return stageMap[stage.toLowerCase()];
  return stage.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Memoized card to prevent re-renders when other columns update
const SortableCard = memo(function SortableCard({ opportunity, onClick, formatCurrency }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: opportunity.canonical_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  // Get display stage (custom_stage or stage)
  const displayStage = opportunity.custom_stage || opportunity.stage || 'Unknown';
  const probability = opportunity.user_probability || opportunity.probability || 0;
  
  // Risk color based on probability
  const probColor = probability >= 60 ? 'text-emerald-600 bg-emerald-50' : 
                    probability >= 40 ? 'text-amber-600 bg-amber-50' : 
                    'text-red-600 bg-red-50';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border p-3 shadow-sm hover:shadow-md transition-all hover:border-blue-200 cursor-pointer group"
      data-testid={`opps-card-${opportunity.canonical_id}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="mt-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate text-gray-900">{opportunity.name}</h4>
          <p className="text-xs text-gray-500 truncate mt-1">
            <Building2 className="h-3 w-3 inline mr-1" />
            {opportunity.account_name || 'No Account'}
          </p>
          
          {/* Stage Badge */}
          <Badge variant="outline" className="text-xs mt-2 mb-2">
            {displayStage}
          </Badge>
          
          {/* Amount - more prominent */}
          <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 rounded">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <span className="text-base font-bold text-gray-900">
              {formatCurrency(opportunity.amount || opportunity.sale_value || 0)}
            </span>
          </div>
          
          {/* Bottom row: Owner + Probability + Close Date + NO-SO Alert */}
          <div className="flex items-center justify-between mt-3 pt-2 border-t">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <User className="h-3 w-3 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-600 truncate max-w-[80px]">
                {opportunity.owner_name || 'Unassigned'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Expected close date */}
              {(opportunity.close_date || opportunity.date_deadline) && (
                <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                  <Calendar className="h-2.5 w-2.5" />
                  {new Date(opportunity.close_date || opportunity.date_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
              <Badge className={`text-xs font-semibold ${probColor}`}>
                {probability}%
              </Badge>
            </div>
          </div>
          {/* SO status for Won deals on Kanban */}
          {(opportunity.stage === 'Won' || opportunity.stage === 'won' || opportunity.stage === 'closed_won') && (
            <div className="mt-1.5">
              {opportunity.so_count > 0 ? (
                <span className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                  ✓ SO: {opportunity.so_count} ({opportunity.so_billing_status === 'fully_invoiced' ? 'Fully Invoiced' : opportunity.so_billing_status === 'partially_invoiced' ? 'Partially Invoiced' : 'Not Invoiced'})
                </span>
              ) : (
                <span className="text-[9px] bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full font-semibold animate-pulse">
                  ⚠️ No SO created yet
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}); // end SortableCard memo

// Memoized column to prevent full re-render when sibling columns change
const KanbanColumn = memo(function KanbanColumn({ stage, opportunities, onCardClick, formatCurrency }) {
  const KANBAN_PAGE_SIZE = 15;
  const [visibleCount, setVisibleCount] = useState(KANBAN_PAGE_SIZE);
  // Filter by custom_stage first, then fall back to stage
  const stageOpps = opportunities.filter(o => {
    const oppStage = (o.custom_stage || o.stage || '').toLowerCase().replace(/[&\s]/g, '_');
    const targetStage = stage.toLowerCase().replace(/[&\s]/g, '_');
    return oppStage === targetStage || o.stage === stage;
  });
  
  // Only render visible cards — this is the key performance optimization
  const visibleOpps = stageOpps.slice(0, visibleCount);
  const hasMore = stageOpps.length > visibleCount;
  
  // Stage header colors
  const stageHeaderColors = {
    qualified: 'border-l-blue-500',
    proposal: 'border-l-purple-500',
    negotiation: 'border-l-amber-500',
    review_negotiation: 'border-l-amber-500',
    closed_won: 'border-l-emerald-500',
    won: 'border-l-emerald-500',
    closed_lost: 'border-l-red-500',
    lost: 'border-l-red-500',
  };
  
  const headerColor = stageHeaderColors[stage] || 'border-l-gray-400';
  const totalValue = stageOpps.reduce((sum, o) => sum + (o.amount || o.sale_value || 0), 0);
  
  return (
    <div 
      className="flex-shrink-0 w-80 bg-gray-50 rounded-lg shadow-sm"
      data-testid={`opps-kanban-column-${stage}`}
    >
      <div className={`p-4 border-b border-l-4 ${headerColor} bg-white rounded-t-lg`}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm text-gray-800">{formatStage(stage)}</h3>
          <Badge className={stageColors[stage] || 'bg-gray-100 text-gray-700'}>
            {stageOpps.length}
          </Badge>
        </div>
        <p className="text-sm font-medium text-gray-600 mt-1">
          {formatCurrency(totalValue)}
        </p>
      </div>
      <ScrollArea className="h-[calc(100vh-320px)]">
        <div className="p-3 space-y-3">
          <SortableContext items={visibleOpps.map(o => o.canonical_id)} strategy={verticalListSortingStrategy}>
            {visibleOpps.map((opp) => (
              <SortableCard 
                key={opp.canonical_id} 
                opportunity={opp} 
                onClick={() => onCardClick(opp)}
                formatCurrency={formatCurrency}
              />
            ))}
          </SortableContext>
          {stageOpps.length === 0 && (
            <div className="py-8 text-center text-gray-400 text-sm">
              No opportunities
            </div>
          )}
          {hasMore && (
            <button
              className="w-full py-2 text-xs text-[#800000] hover:bg-[#800000]/5 rounded-lg font-medium transition-colors"
              onClick={() => setVisibleCount(c => c + KANBAN_PAGE_SIZE)}
              data-testid={`load-more-${stage}`}
            >
              Show {Math.min(stageOpps.length - visibleCount, KANBAN_PAGE_SIZE)} more ({stageOpps.length - visibleCount} remaining)
            </button>
          )}
        </div>
      </ScrollArea>
    </div>
  );
})



export function OpportunitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [opportunities, setOpportunities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(100);
  const [kanbanData, setKanbanData] = useState({ stages: [], data: {} });
  const [opportunitiesSummary, setOpportunitiesSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [activeId, setActiveId] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [odooBaseUrl, setOdooBaseUrl] = useState('');
  const { formatCurrency } = useCurrency();
  const [filterOptions, setFilterOptions] = useState({ years: [], salesReps: [], accounts: [], stages: [], productDirectors: [], solutionCategories: [] });
  
  // Column selection (saved to localStorage)
  const ALL_COLUMNS = [
    { key: 'name', label: 'Name', default: true },
    { key: 'account', label: 'Account', default: true },
    { key: 'stage', label: 'Stage', default: true },
    { key: 'amount', label: 'Amount', default: true },
    { key: 'owner', label: 'Owner', default: true },
    { key: 'probability', label: 'Probability', default: true },
    { key: 'days', label: 'Last Updated', default: true },
    { key: 'product_manager', label: 'Product Director', default: false },
    { key: 'solution_category', label: 'Solution Category', default: false },
    { key: 'presales', label: 'Pre-sales', default: false },
    { key: 'lead_source', label: 'Lead Source', default: false },
    { key: 'campaign', label: 'Campaign', default: false },
    { key: 'create_date', label: 'Created Date', default: false },
  ];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try { const saved = localStorage.getItem('opp_columns'); return saved ? JSON.parse(saved) : ALL_COLUMNS.filter(c => c.default).map(c => c.key); }
    catch { return ALL_COLUMNS.filter(c => c.default).map(c => c.key); }
  });
  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key) ? visibleColumns.filter(k => k !== key) : [...visibleColumns, key];
    setVisibleColumns(next); localStorage.setItem('opp_columns', JSON.stringify(next));
  };
  
  // Sorting
  const [sortField, setSortField] = useState('days_since_update');
  const [sortOrder, setSortOrder] = useState('asc');
  const toggleSort = (field) => {
    if (sortField === field) { setSortOrder(o => o === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortOrder('asc'); }
  };
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  // Initialize filters from URL params (including dashboard navigation)
  const getInitialFilters = () => {
    const stageExclude = searchParams.get('stage_exclude');
    return {
      year: searchParams.get('year') || null,
      quarter: searchParams.get('quarter') || null,
      salesRep: searchParams.get('salesRep') || null,
      account: searchParams.get('account') || null,
      stage: searchParams.get('stage') || (stageExclude ? `exclude:${stageExclude}` : null),
      productDirector: searchParams.get('productDirector') || null,
      solutionCategory: searchParams.get('solutionCategory') || null,
      hasSO: searchParams.get('hasSO') || null,
      soStatus: searchParams.get('soStatus') || null,
    };
  };

  // Contextual filters for Opportunities page
  const [filters, setFilters] = useState(getInitialFilters);

  // Update URL when filters change

  // ── Sort helper: header with up/down arrows ──────────────────────────────
  const SortHead = ({ field, children, className = '' }) => {
    const isActive = sortField === field;
    return (
      <TableHead
        className={`cursor-pointer select-none hover:bg-slate-50 transition-colors ${className}`}
        onClick={() => toggleSort(field)}
      >
        <span className="flex items-center gap-1 group">
          <span>{children}</span>
          <span className="flex flex-col -space-y-0.5 shrink-0">
            <ChevronUp className={`h-2.5 w-2.5 transition-colors ${isActive && sortOrder === 'asc' ? 'text-[#800000]' : 'text-gray-300 group-hover:text-gray-400'}`} />
            <ChevronDown className={`h-2.5 w-2.5 transition-colors ${isActive && sortOrder === 'desc' ? 'text-[#800000]' : 'text-gray-300 group-hover:text-gray-400'}`} />
          </span>
        </span>
      </TableHead>
    );
  };


  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams, { replace: true });
  };

  const applyOpportunityDrilldown = (patch) => {
    setActiveTab('list');
    setCurrentPage(0);
    Object.entries(patch).forEach(([key, value]) => updateFilter(key, value));
  };

  const resetFilters = () => {
    setFilters({ year: null, quarter: null, salesRep: null, account: null, stage: null, productDirector: null, solutionCategory: null, hasSO: null, soStatus: null });
    setCurrentPage(0);
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(v => v !== null);
  };

  // Re-read URL params when they change externally (e.g., from dashboard click)
  useEffect(() => {
    const newFilters = getInitialFilters();
    setFilters(newFilters);
    // Fetch Odoo base URL once for deeplinks
    crmAPI.getSalesOrdersOdooConfig().then(r => setOdooBaseUrl(r.data?.odoo_base_url || '')).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const [filterRes, pmRes, catRes] = await Promise.allSettled([
        analyticsAPI.getFilters(),
        targetAPI.getProductManagers(),
        targetAPI.getSolutionCategories(),
      ]);
      const fData = filterRes.status === 'fulfilled' ? filterRes.value.data : {};
      const pms = pmRes.status === 'fulfilled' ? pmRes.value.data : [];
      const cats = catRes.status === 'fulfilled' ? catRes.value.data : [];
      setFilterOptions({
        years: fData.years || [],
        salesReps: fData.sales_reps || fData.salesReps || [],
        accounts: (fData.accounts || []).map(a => typeof a === 'object' ? a.name : a).filter(Boolean),
        stages: fData.stages || [],
        productDirectors: pms.map(p => p.name),
        solutionCategories: cats.map(c => c.name),
      });
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  }, []);

  // Export to Excel functionality
  const handleExportExcel = async () => {
    try {
      toast.info('Preparing export...');
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.salesRep) params.sales_rep = filters.salesRep;
      if (filters.account) params.account = filters.account;
      if (filters.productDirector) params.product_director = filters.productDirector;
      if (filters.solutionCategory) params.solution_category = filters.solutionCategory;
      // Pass stage filter — preserves "exclude:Won,Lost,Hold" format for backend parsing
      if (filters.stage) params.stage = filters.stage;
      if (filters.hasSO) params.has_so = filters.hasSO;
      if (filters.soStatus) params.so_status = filters.soStatus;
      
      const response = await crmAPI.exportOpportunities(params);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filenameParts = ['opportunities'];
      if (filters.year) filenameParts.push(filters.year);
      if (filters.quarter) filenameParts.push(filters.quarter);
      if (filters.stage && !filters.stage.startsWith('exclude:')) filenameParts.push(filters.stage);
      link.setAttribute('download', `${filenameParts.join('_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export complete!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export opportunities');
    }
  };

  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.year, filters.quarter, filters.salesRep, filters.account, filters.stage, filters.productDirector, filters.solutionCategory, filters.hasSO, filters.soStatus, debouncedSearch, currentPage]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // When switching to Kanban, load kanban data if not already loaded
  useEffect(() => {
    if (activeTab === 'kanban') {
      const params = {};
      if (filters.year) params.year = filters.year;
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.salesRep) params.sales_rep = filters.salesRep;
      if (filters.account) params.account = filters.account;
      if (filters.stage) params.stage = filters.stage;
      if (filters.productDirector) params.product_manager = filters.productDirector;
      if (filters.solutionCategory) params.solution_category = filters.solutionCategory;
      if (filters.hasSO) params.has_so = filters.hasSO;
      if (filters.soStatus) params.so_status = filters.soStatus;
      if (debouncedSearch) params.search = debouncedSearch;
      crmAPI.getKanban(params).then(r => setKanbanData(r.data)).catch(() => {});
    }
  }, [activeTab, filters.year, filters.quarter, filters.salesRep, filters.account, filters.stage, filters.productDirector, filters.solutionCategory, filters.hasSO, filters.soStatus, debouncedSearch]); // eslint-disable-line

  const loadData = async () => {
    try {
      // Build filter params
      const params = { limit: pageSize, skip: currentPage * pageSize };
      if (filters.year) params.year = filters.year;
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.salesRep) params.sales_rep = filters.salesRep;
      if (filters.account) params.account = filters.account;
      if (filters.stage) params.stage = filters.stage;
      if (filters.productDirector) params.product_manager = filters.productDirector;
      if (filters.solutionCategory) params.solution_category = filters.solutionCategory;
      if (filters.hasSO) params.has_so = filters.hasSO;
      if (filters.soStatus) params.so_status = filters.soStatus;
      if (debouncedSearch) params.search = debouncedSearch;

      const [listRes, kanbanRes] = await Promise.all([
        crmAPI.listOpportunities(params),
        activeTab === 'kanban' ? crmAPI.getKanban(params) : Promise.resolve({ data: kanbanData }),
      ]);
      // Handle paginated response format { items, total, limit, skip, has_more }
      const listData = listRes.data;
      if (listData && listData.items) {
        setOpportunities(listData.items);
        setTotalCount(listData.total || 0);
        setOpportunitiesSummary(listData.summary || {});
      } else {
        // Fallback for old array format
        setOpportunities(Array.isArray(listData) ? listData : []);
        setTotalCount(Array.isArray(listData) ? listData.length : 0);
        setOpportunitiesSummary({});
      }
      setKanbanData(kanbanRes.data);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over) return;
    
    const overElement = document.querySelector(`[data-testid*="${over.id}"]`);
    if (!overElement) return;
    
    const columnElement = overElement.closest('[data-testid^="opps-kanban-column-"]');
    if (!columnElement) return;
    
    const newStage = columnElement.getAttribute('data-testid').replace('opps-kanban-column-', '');
    const opp = opportunities.find(o => o.canonical_id === active.id);
    
    if (opp && opp.stage !== newStage) {
      try {
        await crmAPI.updateStage(active.id, newStage);
        toast.success('Stage updated');
        loadData();
      } catch (error) {
        toast.error('Failed to update stage');
      }
    }
  };

  const handleOpenDetail = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setDetailSheetOpen(true);
  };

  const allOpportunities = useMemo(
    () => Object.values(kanbanData?.data || {}).flat(),
    [kanbanData]
  );

  const filteredOpportunities = useMemo(() =>
    !searchQuery
      ? allOpportunities
      : allOpportunities.filter(opp =>
          opp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opp.account_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opp.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opp.product_manager?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          opp.solution_category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (opp.custom_stage || opp.stage || '').toLowerCase().includes(searchQuery.toLowerCase())
        ),
    [allOpportunities, searchQuery]
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-1" data-testid="opportunities-page">
      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
          <p className="text-gray-500">Manage and track your sales opportunities</p>
        </div>
        <div className="flex items-center gap-2">
          <RoleScopeControl dataTestId="opportunities-role-scope-control" />
          <CompactBusinessRules
            scopeItems={[filters.year, filters.quarter, filters.salesRep, filters.account].filter(Boolean)}
            ruleKeys={['open_pipeline', 'won_lost_hold', 'totals_scope']}
            label="Rules"
            dataTestId="opportunities-compact-business-rules"
          />
        </div>
      </div>

      {/* Contextual Filters for Opportunities */}
      <PageFilters 
        onReset={resetFilters}
        activeFilters={[filters.year, filters.quarter, filters.salesRep, filters.account, filters.stage, filters.hasSO, filters.soStatus]}
        title="Opportunity Filters"
      >
        <YearFilter 
          value={filters.year} 
          onChange={(v) => updateFilter('year', v)} 
          years={filterOptions.years}
        />
        <QuarterFilter 
          value={filters.quarter} 
          onChange={(v) => updateFilter('quarter', v)} 
        />
        <SalesRepFilter 
          value={filters.salesRep} 
          onChange={(v) => updateFilter('salesRep', v)} 
          salesReps={filterOptions.salesReps}
        />
        <AccountFilter 
          value={filters.account} 
          onChange={(v) => updateFilter('account', v)} 
          accounts={filterOptions.accounts}
        />
        <StageFilter 
          value={filters.stage} 
          onChange={(v) => updateFilter('stage', v)} 
          stages={filterOptions.stages}
        />
        <ProductDirectorFilter
          value={filters.productDirector}
          onChange={(v) => updateFilter('productDirector', v)}
          productDirectors={filterOptions.productDirectors}
        />
        <SolutionCategoryFilter
          value={filters.solutionCategory}
          onChange={(v) => updateFilter('solutionCategory', v)}
          categories={filterOptions.solutionCategories}
        />
        <Select value={filters.hasSO || '_all'} onValueChange={(v) => updateFilter('hasSO', v === '_all' ? null : v)}>
          <SelectTrigger className="w-36 h-10" data-testid="opps-has-so-filter"><SelectValue placeholder="SO Link" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All SO States</SelectItem>
            <SelectItem value="with_so">With SO</SelectItem>
            <SelectItem value="without_so">Without SO</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.soStatus || '_all'} onValueChange={(v) => updateFilter('soStatus', v === '_all' ? null : v)}>
          <SelectTrigger className="w-44 h-10" data-testid="opps-so-status-filter"><SelectValue placeholder="SO Invoicing" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Invoice Progress</SelectItem>
            <SelectItem value="fully_invoiced">Fully Invoiced</SelectItem>
            <SelectItem value="partially_invoiced">Partially Invoiced</SelectItem>
            <SelectItem value="not_invoiced">Not Invoiced</SelectItem>
          </SelectContent>
        </Select>
      </PageFilters>
      <FilterChipBar
        filters={filters}
        onClear={(key) => updateFilter(key, null)}
        onClearAll={resetFilters}
        onOpenAdvanced={() => setShowAdvancedFilter(true)}
      />
      <DataFreshnessBar entityIds={['opportunities', 'sales_orders', 'log_messages']} actionEntityIds={['opportunities']} title="Opportunity Data Freshness" dataTestId="opportunities-data-freshness" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          { label: 'Total Pipeline', value: formatCurrency(totalCount ? (opportunitiesSummary.total_amount || 0) : 0), tone: 'text-[#800000] bg-[#800000]/10', action: () => applyOpportunityDrilldown({ hasSO: null, soStatus: null }) },
          { label: 'With SO', value: opportunitiesSummary.with_so_count || 0, tone: 'text-emerald-700 bg-emerald-100', action: () => applyOpportunityDrilldown({ hasSO: 'with_so' }) },
          { label: 'Without SO', value: opportunitiesSummary.without_so_count || 0, tone: 'text-red-700 bg-red-100', action: () => applyOpportunityDrilldown({ hasSO: 'without_so' }) },
          { label: 'Fully Invoiced', value: opportunitiesSummary.fully_invoiced_count || 0, tone: 'text-emerald-700 bg-emerald-50', action: () => applyOpportunityDrilldown({ hasSO: 'with_so', soStatus: 'fully_invoiced' }) },
          { label: 'Partial SO', value: opportunitiesSummary.partially_invoiced_count || 0, tone: 'text-amber-700 bg-amber-50', action: () => applyOpportunityDrilldown({ hasSO: 'with_so', soStatus: 'partially_invoiced' }) },
          { label: 'Not Invoiced', value: opportunitiesSummary.not_invoiced_count || 0, tone: 'text-slate-700 bg-slate-100', action: () => applyOpportunityDrilldown({ hasSO: 'with_so', soStatus: 'not_invoiced' }) },
        ].map(card => (
          <Card key={card.label} className="rounded-2xl border-slate-200 shadow-sm cursor-pointer hover:shadow-md hover:border-[#800000]/30" onClick={card.action} data-testid={`opps-summary-card-${card.label.toLowerCase().replace(/\s+/g,'-')}`}><CardContent className="pt-4 pb-3"><p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-semibold">{card.label}</p><p className="text-xl font-bold text-slate-900 mt-2">{card.value}</p><div className={`inline-flex mt-2 rounded-full px-2 py-1 text-[10px] font-medium ${card.tone}`}>{card.label}</div><p className="text-[10px] text-[#800000] mt-2">Click to filter</p></CardContent></Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <TabsList className="w-full xl:w-auto overflow-x-auto">
            <TabsTrigger value="list" data-testid="opps-tab-list">View: List</TabsTrigger>
            <TabsTrigger value="kanban" data-testid="opps-tab-kanban">View: Kanban</TabsTrigger>
          </TabsList>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportExcel}
              className="flex items-center gap-2"
              data-testid="export-excel-btn"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Popover open={showColumnPicker} onOpenChange={setShowColumnPicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-picker-btn">
                  <Settings2 className="h-4 w-4 mr-1" /> Columns
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2">
                <p className="text-xs font-semibold text-gray-500 mb-2">Show/Hide Columns</p>
                {ALL_COLUMNS.map(col => (
                  <button key={col.key} onClick={() => toggleColumn(col.key)}
                    className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 flex items-center gap-2 ${visibleColumns.includes(col.key) ? 'text-gray-900' : 'text-gray-400'}`}>
                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px] ${visibleColumns.includes(col.key) ? 'bg-[#800000] border-[#800000] text-white' : 'border-gray-300'}`}>
                      {visibleColumns.includes(col.key) ? '✓' : ''}
                    </span>
                    {col.label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                data-testid="opps-search-input"
              />
            </div>
          </div>
        </div>

        <TabsContent value="list" className="mt-6">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {visibleColumns.includes('name') && <SortHead field="name">Name</SortHead>}
                  {visibleColumns.includes('account') && <SortHead field="account_name">Account</SortHead>}
                  {visibleColumns.includes('stage') && <SortHead field="stage">Stage</SortHead>}
                  {visibleColumns.includes('amount') && <SortHead field="sale_value" className="text-right">Amount</SortHead>}
                  {visibleColumns.includes('owner') && <SortHead field="owner_name">Owner</SortHead>}
                  {visibleColumns.includes('probability') && <SortHead field="probability" className="w-32">Probability</SortHead>}
                  {visibleColumns.includes('product_manager') && <SortHead field="product_manager">Product Director</SortHead>}
                  {visibleColumns.includes('solution_category') && <SortHead field="solution_category">Solution Category</SortHead>}
                  {visibleColumns.includes('presales') && <SortHead field="presales_engineer">Pre-sales</SortHead>}
                  {visibleColumns.includes('lead_source') && <SortHead field="lead_source">Lead Source</SortHead>}
                  {visibleColumns.includes('campaign') && <SortHead field="campaign">Campaign</SortHead>}
                  {visibleColumns.includes('create_date') && <SortHead field="create_date">Created</SortHead>}
                  {visibleColumns.includes('days') && <SortHead field="days_since_update">Last Updated</SortHead>}
                  <TableHead className="text-right w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.length === 0 ? (
                  <TableRow><TableCell colSpan={visibleColumns.length + 1} className="text-center py-8 text-gray-500">No opportunities found.</TableCell></TableRow>
                ) : (
                  opportunities
                    .sort((a, b) => { let va = a[sortField] ?? (typeof b[sortField] === 'number' ? 9999 : ''); let vb = b[sortField] ?? (typeof a[sortField] === 'number' ? 9999 : ''); if (typeof va === 'number' && typeof vb === 'number') return sortOrder === 'asc' ? va - vb : vb - va; return sortOrder === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va)); })
                    .map((opp) => (
                      <TableRow key={opp.canonical_id} className="cursor-pointer hover:bg-gray-50" onClick={() => handleOpenDetail(opp)}>
                        {visibleColumns.includes('name') && <TableCell className="font-medium">{opp.name}</TableCell>}
                        {visibleColumns.includes('account') && <TableCell>{opp.account_name || '-'}</TableCell>}
                        {visibleColumns.includes('stage') && <TableCell><Badge className={stageColors[opp.custom_stage?.toLowerCase().replace(/[&\s]/g, '_')] || stageColors[opp.stage] || 'bg-gray-100 text-gray-700'}>{opp.custom_stage || formatStage(opp.stage)}</Badge></TableCell>}
                        {visibleColumns.includes('amount') && <TableCell className="text-right font-mono">{formatCurrency(opp.sale_value || opp.amount || 0)}</TableCell>}
                        {visibleColumns.includes('owner') && <TableCell>{opp.owner_name || '-'}</TableCell>}
                        {visibleColumns.includes('probability') && <TableCell><div className="flex items-center gap-2"><Progress value={opp.user_probability || opp.probability || 0} className="w-16 h-2" /><span className="text-sm">{opp.user_probability || opp.probability || 0}%</span></div></TableCell>}
                        {visibleColumns.includes('product_manager') && <TableCell className="text-xs">{opp.product_manager || '-'}</TableCell>}
                        {visibleColumns.includes('solution_category') && <TableCell className="text-xs">{opp.solution_category || '-'}</TableCell>}
                        {visibleColumns.includes('presales') && <TableCell>{opp.presales_engineer ? <span className="text-xs text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">{opp.presales_engineer}</span> : <span className="text-xs text-gray-300">-</span>}</TableCell>}
                        {visibleColumns.includes('lead_source') && <TableCell>{opp.lead_source ? <span className="text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{opp.lead_source}</span> : <span className="text-xs text-gray-300">-</span>}</TableCell>}
                        {visibleColumns.includes('campaign') && <TableCell className="text-xs">{opp.campaign_name || '-'}</TableCell>}
                        {visibleColumns.includes('create_date') && <TableCell className="text-xs text-gray-500">{(opp.create_date || '').split(' ')[0]}</TableCell>}
                        {visibleColumns.includes('days') && <TableCell>{opp.days_since_update != null ? (<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${opp.days_since_update <= 7 ? 'bg-green-100 text-green-700' : opp.days_since_update <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{opp.days_since_update}d ago</span>) : <span className="text-xs text-gray-400">-</span>}</TableCell>}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {/* SO status badge */}
                            {opp.so_count > 0 && (
                              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${
                                opp.so_billing_status === 'fully_invoiced' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                opp.so_billing_status === 'partially_invoiced' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-700 border-slate-200'
                              }`} data-testid={`opp-so-badge-${opp.canonical_id}`} title={`${opp.so_count} Sales Order(s) — ${opp.so_billing_status || 'No billing status'}`}>
                                SO:{opp.so_count}
                              </span>
                            )}
                            {/* ⚠️ Won deals with NO SO — alert with days since won */}
                            {opp.so_count === 0 && (opp.stage === 'Won' || opp.stage === 'won' || opp.stage === 'closed_won') && (() => {
                              const wonDate = opp.date_last_stage_update ? new Date(opp.date_last_stage_update) : null;
                              const daysSinceWon = wonDate ? Math.floor((Date.now() - wonDate.getTime()) / 86400000) : null;
                              return (
                                <span
                                  className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200 animate-pulse"
                                  title={`Won ${daysSinceWon ? daysSinceWon + ' days ago' : ''} — No Sales Order created yet! Create SO in Odoo.`}
                                  data-testid={`opp-no-so-alert-${opp.canonical_id}`}
                                >
                                  ⚠️ No SO{daysSinceWon ? ` ${daysSinceWon}d` : ''}
                                </span>
                              );
                            })()}
                            {opp.source_record_id && odooBaseUrl && (
                              <a href={`${odooBaseUrl}/web#id=${opp.source_record_id}&model=crm.lead&view_type=form`}
                                target="_blank" rel="noopener noreferrer"
                                title="Open in Odoo"
                                className="inline-flex items-center justify-center h-7 w-7 text-muted-foreground hover:text-[#800000] transition-colors"
                                data-testid={`opp-odoo-link-${opp.canonical_id}`}
                                onClick={e => e.stopPropagation()}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDetail(opp); }}>View</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                )}
                {/* ── SUMMARY ROW ── sticky at bottom with totals */}
                {opportunities.length > 0 && (() => {
                  const filteredOpps = opportunities;
                  const totalAmount = filteredOpps.reduce((s, o) => s + (o.sale_value || o.amount || 0), 0);
                  const totalExpected = filteredOpps.reduce((s, o) => s + (o.expected_revenue || 0), 0);
                  const avgProb = filteredOpps.length > 0
                    ? Math.round(filteredOpps.reduce((s, o) => s + (o.user_probability || o.probability || 0), 0) / filteredOpps.length)
                    : 0;
                  const colSpan = visibleColumns.length + 1;
                  return (
                    <TableRow className="border-t-2 border-slate-300 bg-slate-50 font-bold sticky bottom-0" data-testid="opportunities-summary-row">
                      {visibleColumns.includes('name') && (
                        <TableCell className="text-xs font-bold text-slate-700 py-2.5">
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#800000] inline-block" />
                            TOTAL ({filteredOpps.length} opps)
                          </span>
                        </TableCell>
                      )}
                      {visibleColumns.includes('account') && <TableCell />}
                      {visibleColumns.includes('stage') && <TableCell />}
                      {visibleColumns.includes('amount') && (
                        <TableCell className="text-right">
                          <div>
                            <p className="font-bold text-[#800000] font-mono text-sm">{formatCurrency(totalAmount)}</p>
                            {totalExpected > 0 && totalExpected !== totalAmount && (
                              <p className="text-[10px] text-slate-400 font-normal font-mono">Exp: {formatCurrency(totalExpected)}</p>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {visibleColumns.includes('owner') && <TableCell />}
                      {visibleColumns.includes('probability') && (
                        <TableCell>
                          <span className="text-xs font-semibold text-slate-600">Avg {avgProb}%</span>
                        </TableCell>
                      )}
                      {visibleColumns.includes('product_manager') && <TableCell />}
                      {visibleColumns.includes('solution_category') && <TableCell />}
                      {visibleColumns.includes('presales') && <TableCell />}
                      {visibleColumns.includes('lead_source') && <TableCell />}
                      {visibleColumns.includes('campaign') && <TableCell />}
                      {visibleColumns.includes('create_date') && <TableCell />}
                      {visibleColumns.includes('days') && <TableCell />}
                      <TableCell />
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
            {/* Pagination */}
            {totalCount > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="opportunities-pagination">
                <p className="text-sm text-gray-500">
                  Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)} of <span className="font-semibold">{totalCount.toLocaleString()}</span> opportunities
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={currentPage === 0}
                    onClick={() => { setCurrentPage(p => p - 1); setLoading(true); }}>
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">Page {currentPage + 1} of {Math.ceil(totalCount / pageSize)}</span>
                  <Button variant="outline" size="sm" disabled={(currentPage + 1) * pageSize >= totalCount}
                    onClick={() => { setCurrentPage(p => p + 1); setLoading(true); }}>
                    Next
                  </Button>
                </div>
              </div>
            )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="kanban" className="mt-6">
          <DndContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
              {STAGES.map((stage) => (
                <KanbanColumn
                  key={stage}
                  stage={stage}
                  opportunities={filteredOpportunities}
                  onCardClick={handleOpenDetail}
                  formatCurrency={formatCurrency}
                />
              ))}
            </div>
          </DndContext>
        </TabsContent>
      </Tabs>

      <OpportunityDetailSheet
        opportunity={selectedOpportunity}
        open={detailSheetOpen}
        onClose={() => setDetailSheetOpen(false)}
        formatCurrency={formatCurrency}
        odooBaseUrl={odooBaseUrl}
      />
      <AdvancedFilterBuilder open={showAdvancedFilter} onClose={() => setShowAdvancedFilter(false)}
        currentFilters={filters}
        filterOptions={{ productDirectors: filterOptions.productDirectors, solutionCategories: filterOptions.solutionCategories, salespersons: filterOptions.salesReps }}
        onApply={(newFilters) => { setFilters(prev => ({ ...prev, ...newFilters })); setCurrentPage(0); }}
      />
    </div>
  );
}
